import AuthContext from "context/AuthContext";
import ProfileContext from "context/ProfileContext";
import React, { useContext, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AgreementTC from "../AgreementTC";
import BasicInfo from "../ProfileCompletion/BasicInfo";
import ProfileInfo from "../ProfileCompletion/ProfileInfo";
import ShopifyDetail from "./ShopifyDetail";
import StoreDetail from "./StoreDetail";
import PurebrandLoader from "components/Loader/PurebrandLoader";
import { message } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { STORE_PROFILE } from "../constants";
import {
  mapBackendStepsToFrontend,
  STORE_ONBOARDING_STEP_CONFIG,
  getCompletedStepHashes,
  getStepHashForStep,
} from "utils/onboardingStepMapper";

/** Use form value if it's a valid number (including 0); else saved; else default. Avoids storing defaults when user entered a value. */
function numberOrFallback(formVal, savedVal, defaultVal) {
  const n = formVal != null && formVal !== "" ? Number(formVal) : NaN;
  if (Number.isFinite(n)) return n;
  const s = savedVal != null && savedVal !== "" ? Number(savedVal) : NaN;
  if (Number.isFinite(s)) return s;
  return defaultVal != null ? Number(defaultVal) : 0;
}

const StoreProfileForm = () => {
  const normalizeStepHash = (stepHash) => {
    if (stepHash === "shopify-accounts") return "shopify-details";
    return stepHash;
  };

  let { user, setOnboardingStepCompletion, onboardingStepCompletion, updateProfile } = useContext(AuthContext);
  let { fetchPreviousStepData, formDataState, setFormDataState, updateHalfProfile, putToS3, setupStoreProfile } =
    useContext(ProfileContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [messageApi, contextHolder] = message.useMessage();

  const savedData = JSON.parse(localStorage.getItem("userProfile")) || {};
  const completedStepsFromStorage = localStorage.getItem("onboardingStepCompletion")
    ? JSON.parse(localStorage.getItem("onboardingStepCompletion"))
    : {};

  const stepComponentMap = {
    "basic-info": BasicInfo,
    "profile-info": ProfileInfo,
    "store-details": StoreDetail,
    "shopify-details": ShopifyDetail,
    "agreement": AgreementTC,
  };

  // Form steps only (exclude sidebar redirect-only steps)
  const stepsConfig = STORE_ONBOARDING_STEP_CONFIG
    .filter((s) => !["dots-account", "communications"].includes(s.hash))
    .map((step) => ({
    ...step,
    component: stepComponentMap[step.hash],
  }));
  
  // Create hash-to-component mapping for label-based step tracking
  const hashToComponentMap = stepsConfig.reduce((map, step) => {
    map[step.hash] = step.component;
    return map;
  }, {});
  
  const getCompletionMap = () => {
    if (onboardingStepCompletion && Object.keys(onboardingStepCompletion).length > 0) {
      return onboardingStepCompletion;
    }
    return completedStepsFromStorage || {};
  };

  const isStepCompleted = (stepHash) => {
    // 1. Check if hash is in userProfile.onboarding_completed_steps (User's preferred source of truth)
    const profileCompletedSteps = savedData?.onboarding_completed_steps || [];
    if (Array.isArray(profileCompletedSteps) && profileCompletedSteps.includes(stepHash)) {
      return true;
    }
    
    // 2. Fallback to onboardingStepCompletion context/storage
    const completionMap = getCompletionMap();
    return completionMap[stepHash] === true;
  };

  const getFirstIncompleteStep = () => {
    const firstIncomplete = stepsConfig.find((step) => !isStepCompleted(step.hash));
    if (firstIncomplete?.hash) return firstIncomplete.hash;
    // If everything is complete, don't bounce back to the first step (agreement).
    // Default to the last step so the UI stays stable.
    return stepsConfig[stepsConfig.length - 1]?.hash || "agreement";
  };

  /** True when agreement step is completed (required before other steps are enabled). */
  const isAgreementComplete = () => isStepCompleted("agreement");

  /** True when every step in the full config (including dots-account) is completed. */
  const areAllOnboardingStepsComplete = () => {
    return STORE_ONBOARDING_STEP_CONFIG.every((step) => isStepCompleted(step.hash));
  };

  const getInitialStep = () => {
    if (location.hash) {
      const hashValue = normalizeStepHash(location.hash.substring(1));
      if (hashToComponentMap[hashValue]) {
        if (hashValue === "agreement") {
          return hashValue;
        }
        if (isAgreementComplete()) {
          return hashValue;
        }
      }
    }
    return getFirstIncompleteStep();
  };
  
  const [currentStep, setCurrentStep] = useState(getInitialStep());
  const [editingStepHash, setEditingStepHash] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const isRedirectingToWelcomeRef = useRef(false);

  // Mark step as completed using step hash (label)
  const markStepCompleted = (stepHash) => {
    if (!stepHash) {
      return;
    }
    setOnboardingStepCompletion((prev) => {
      const next = { ...prev, [stepHash]: true };
      localStorage.setItem("onboardingStepCompletion", JSON.stringify(next));
      return next;
    });
  };

  // Update URL hash to match the current step (keeps router in sync; prevents revisiting completed steps)
  const syncHashToStep = React.useCallback(
    (stepHash) => {
      if (!stepHash) return;
      const normalizedStepHash = normalizeStepHash(stepHash);
      const pathname = location.pathname || "/user/completeprofile";
      const search = location.search || "";
      const currentHash = normalizeStepHash((location.hash || "").replace(/^#/, ""));
      if (currentHash !== normalizedStepHash) {
        navigate({ pathname, search, hash: `#${normalizedStepHash}` }, { replace: true });
      }
    },
    [location.pathname, location.search, location.hash, navigate]
  );

  // Navigate to next step by hash, skipping completed steps; update URL hash
  const goToNextStep = () => {
    const currentIndex = stepsConfig.findIndex((s) => s.hash === currentStep);
    
    // 1. First, try to find the next INCOMPLETE step in the flow
    for (let index = currentIndex + 1; index < stepsConfig.length; index += 1) {
      const nextStepHash = stepsConfig[index].hash;
      if (!isStepCompleted(nextStepHash)) {
        setCurrentStep(nextStepHash);
        syncHashToStep(nextStepHash);
        return;
      }
    }
    
    // 2. If no subsequent steps are incomplete, just move to the IMMEDIATELY NEXT step sequentially
    if (currentIndex < stepsConfig.length - 1) {
      const nextStepHash = stepsConfig[currentIndex + 1].hash;
      setCurrentStep(nextStepHash);
      syncHashToStep(nextStepHash);
    }
  };

  // Navigate to previous step by hash
  const goToPreviousStep = () => {
    const currentIndex = stepsConfig.findIndex(s => s.hash === currentStep);
    if (currentIndex > 0) {
      const prevStepHash = stepsConfig[currentIndex - 1].hash;
      setCurrentStep(prevStepHash);
    }
  };

  const canNavigateToStep = (stepHash) => {
    // Only restriction: until agreement is completed, other steps are disabled.
    // Once agreement is complete, any step can be visited/edited anytime.
    if (!isAgreementComplete()) return stepHash === "agreement";
    return true;
  };

  const makeRequest = async (formData) => {
    try {
      const response = await setupStoreProfile(formData);
      if (response?.status === 200) {
        localStorage.removeItem("userProfile");
        localStorage.removeItem("onboardingStepCompletion");
        try {
          await updateProfile();
        } catch (e) {
          console.error("Refresh profile after onboarding failed", e);
        }
        window.location.assign(`${process.env.REACT_APP_URL || ""}/user/welcome`);
        return response;
      }
      return response;
    } catch (err) {
      console.error("Error Occured:", err);
      throw err;
    }
  };

  /** Build full store payload for final onboarding submit (when all steps are complete). */
  const buildCompleteStorePayload = (mergeData) => {
    const d = mergeData || {};
    const discountConfigVal = d.discount_config || savedData.store?.discount_config || null;
    const discountVal = d.discountGiven ?? savedData.store?.discount_given ?? 0;
    const discountTypeVal = d.discountType ?? savedData?.store?.discount_type ?? "AMOUNT";
    return {
      phone_number: d.phone_number || savedData.phone_number || "",
      about_me: d.about_me ?? savedData.about_me ?? "",
      instagram_handle: d.instagram_handle !== undefined ? d.instagram_handle : (savedData.user_social?.instagram_handle ?? ""),
      twitter_handle: d.twitter_handle !== undefined ? d.twitter_handle : (savedData.user_social?.twitter_handle ?? ""),
      tiktok_handle: d.tiktok_handle !== undefined ? d.tiktok_handle : (savedData.user_social?.tiktok_handle ?? ""),
      facebook_handle: d.facebook_handle !== undefined ? d.facebook_handle : (savedData.user_social?.facebook_handle ?? ""),
      latitude: d.latitude ?? savedData.user_geo_location?.latitude ?? "",
      longitude: d.longitude ?? savedData.user_geo_location?.longitude ?? "",
      address: d.address ?? savedData.user_geo_location?.address ?? "",
      street_number: d.street_number ?? savedData.user_geo_location?.street_number ?? "",
      city: d.city ?? savedData.user_geo_location?.city ?? "",
      state: d.state ?? savedData.user_geo_location?.state ?? "",
      zip_code: d.zip_code ?? savedData.user_geo_location?.zip_code ?? "",
      profile_picture_id: d.profile_picture_id ?? savedData.profile_picture?.id ?? null,
      imageFile: d.imageFile || [],
      files: d.files || "",
      videofile: d.videofile || "",
      tag: d.tag || "",
      type: d.type || "",
      extension: d.extension || "",
      store_email: d.store_email || "",
      id: d.id || "",
      social_selected: d.social_selected !== undefined ? d.social_selected : true,
      demoCommission: d.demoCommission ?? savedData.store?.demo_commission ?? null,
      commissionType: d.commissionType ?? savedData?.store?.demo_commission_type ?? "AMOUNT",
      salesCommission: d.salesCommission ?? savedData.store?.demo_sales_commission ?? null,
      salesCommissionType: d.salesCommissionType ?? savedData?.store?.demo_sales_commission_type ?? "AMOUNT",
      discountType: d.discountType ?? savedData?.store?.discount_type ?? "AMOUNT",
      discountGiven: discountVal,
      discount_config: discountConfigVal ? { ...discountConfigVal, discount: discountVal, discount_type: discountTypeVal } : null,
      paymentFrequency: d.paymentFrequency ?? savedData.store?.payment_frequency ?? 1,
      no_show_demo_commission_type: d.no_show_demo_commission_type ?? savedData?.store?.no_show_demo_commission_type ?? "AMOUNT",
      no_show_demo_commission: d.no_show_demo_commission ?? savedData.store?.no_show_demo_commission ?? null,
      shopName: d.shopName || "",
      shipDate: d.shipDate ?? "",
      initialUpdate: true,
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await fetchPreviousStepData(user.uid);
        if (response?.data?.data) {
          setFormDataState((prev) => ({ ...prev, ...response.data.data }));
          const storeName = response.data.store?.store_name;
          localStorage.setItem(
            "userProfile",
            JSON.stringify({ ...response.data.data, initialStoreName: storeName })
          );
          const completedStepsArray =
            response?.data?.data?.onboarding_completed_steps ||
            response?.data?.onboarding_completed_steps ||
            [];
          if (Array.isArray(completedStepsArray) && completedStepsArray.length > 0) {
            const completedStepsObject = mapBackendStepsToFrontend(
              completedStepsArray,
              STORE_ONBOARDING_STEP_CONFIG
            );
            setOnboardingStepCompletion(completedStepsObject);
            localStorage.setItem("onboardingStepCompletion", JSON.stringify(completedStepsObject));
          }
        }
      } catch (error) {
        console.error("Error fetching previous step data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (user?.uid) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // Handle hash-based navigation
  useEffect(() => {
    if (isRedirectingToWelcomeRef.current) return;
    const hashValue = normalizeStepHash((location.hash || "").replace(/^#/, ""));
    if (hashValue && hashToComponentMap[hashValue] && canNavigateToStep(hashValue)) {
      setCurrentStep(hashValue);
      return;
    }
    const firstIncomplete = getFirstIncompleteStep();
    setCurrentStep(firstIncomplete);
    syncHashToStep(firstIncomplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, onboardingStepCompletion]);

  const handleNextStep = async (newData, final = false, half = false) => {
    setFormDataState((prev) => ({ ...prev, ...newData }));

    const completionMap = getCompletionMap();
    const completedHashes = getCompletedStepHashes(completionMap, STORE_ONBOARDING_STEP_CONFIG);
    const currentStepHash = getStepHashForStep(currentStep);
    const onboardingCompletedSteps = currentStepHash && !completedHashes.includes(currentStepHash)
      ? [...completedHashes, currentStepHash]
      : completedHashes;

    if (final) {
      // Agreement submit: only update half-profile (agreement + step). No full profile API or redirect.
      const agreementCompletedSteps = currentStepHash && !completedHashes.includes(currentStepHash)
        ? [...completedHashes, currentStepHash]
        : completedHashes;
      try {
        await updateHalfProfile({
          agreement_accepted: true,
          onboarding_completed_steps: agreementCompletedSteps,
        });
      } catch (e) {
        console.error("Half-profile agreement update failed", e);
        messageApi.open({ type: "error", content: STORE_PROFILE.GENERAL_ERROR, duration: 5 });
        return;
      }
      markStepCompleted(currentStep);
      goToNextStep();
      return;
    }

    if (!final && currentStep === "basic-info") {
      setIsLoading(true);
      try {
        const payload = {
          phone_number: newData.phone_number || savedData.phone_number || "",
          onboarding_completed_steps: onboardingCompletedSteps,
        };
        if (user?.role !== 2) {
          payload.about_me = newData.about_me ?? savedData.about_me ?? "";
          payload.instagram_handle = newData.instagram_handle ?? savedData.user_social?.instagram_handle ?? "";
          payload.twitter_handle = newData.twitter_handle ?? savedData.user_social?.twitter_handle ?? "";
          payload.tiktok_handle = newData.tiktok_handle ?? savedData.user_social?.tiktok_handle ?? "";
          payload.facebook_handle = newData.facebook_handle ?? savedData.user_social?.facebook_handle ?? "";
          payload.latitude = newData.latitude ?? savedData.user_geo_location?.latitude ?? "";
          payload.longitude = newData.longitude ?? savedData.user_geo_location?.longitude ?? "";
          payload.address = newData.address ?? savedData.user_geo_location?.address ?? "";
          payload.street_number = newData.street_number ?? savedData.user_geo_location?.street_number ?? "";
          payload.city = newData.city ?? savedData.user_geo_location?.city ?? "";
          payload.state = newData.state ?? savedData.user_geo_location?.state ?? "";
          payload.zip_code = newData.zip_code ?? savedData.user_geo_location?.zip_code ?? "";
        }
        const res = await updateHalfProfile(payload);
        if (res?.status === 200) {
          if (isStepCompleted(currentStep)) {
            setEditingStepHash(null);
            setIsLoading(false);
            return;
          }
          markStepCompleted(currentStep);
          goToNextStep();
        }
      } catch (e) {
        console.error("Basic info half-profile update failed", e);
        messageApi.open({ type: "error", content: STORE_PROFILE.GENERAL_ERROR, duration: 5 });
      }
      setIsLoading(false);
      return;
    }

    if (half) {
      setIsLoading(true);

      try {
        let profile_picture_id = newData.profile_picture_id ?? savedData.profile_picture?.id ?? null;

        if (currentStep === "shopify-accounts") {
          const shopifyPayload = {
            store: {
              store_name: newData.shopName || savedData.store?.store_name || "",
              logo: newData.files || savedData.store?.logo,
              store_video_id: newData.videofile || savedData.store?.store_video_id
            },
            onboarding_completed_steps: onboardingCompletedSteps,
          };
          const profileRes = await updateHalfProfile(shopifyPayload);
          if (profileRes?.status === 200) {
            if (isStepCompleted(currentStep)) {
              setEditingStepHash(null);
              setIsLoading(false);
              return;
            }
            let newCompletionMap = { ...completionMap, [currentStep]: true };
            const completedStepsArray =
              profileRes?.data?.data?.onboarding_completed_steps ?? profileRes?.data?.onboarding_completed_steps ?? [];
            if (Array.isArray(completedStepsArray) && completedStepsArray.length > 0) {
              const completedStepsObject = mapBackendStepsToFrontend(
                completedStepsArray,
                STORE_ONBOARDING_STEP_CONFIG
              );
              newCompletionMap = completedStepsObject;
              setOnboardingStepCompletion(completedStepsObject);
              localStorage.setItem("onboardingStepCompletion", JSON.stringify(completedStepsObject));
            } else {
              markStepCompleted(currentStep);
            }
            const allComplete = STORE_ONBOARDING_STEP_CONFIG.every((s) => newCompletionMap[s.hash]);
            if (allComplete) {
              isRedirectingToWelcomeRef.current = true;
              try {
                const payload = buildCompleteStorePayload({ ...savedData, ...newData });
                await makeRequest(payload);
              } catch (e) {
                console.error("Final onboarding submit failed", e);
                messageApi.open({ type: "error", content: STORE_PROFILE.GENERAL_ERROR, duration: 5 });
              }
              setIsLoading(false);
              return;
            }
            goToNextStep();
          }
          setIsLoading(false);
          return;
        }

        // Profile-info: upload ONLY if user selected an image; always call half-profile to mark step done
        if (currentStep === "profile-info") {
          if (Array.isArray(newData.imageFile) && newData.imageFile.length > 0) {
            const imageData = {
              tag: "profile_photo",
              type: "image",
              extension: "jpg",
              file: newData.imageFile,
            };
            try {
              const res = await putToS3(imageData);
              if (res.status === 200) {
                profile_picture_id = res.data.data.id;
              } else {
                messageApi.open({
                type: 'error',
                  content: STORE_PROFILE.IMAGE_UPLOAD_ERROR,
                  duration: 5,
                });
                setIsLoading(false);
                return;
              }
            } catch (error) {
              console.error("Error during image upload:", error);
              messageApi.open({
              type: 'error',
                content: STORE_PROFILE.GENERAL_ERROR,
                duration: 5,
              });
              setIsLoading(false);
              return;
            }
          }
          const profileInfoPayload = {
            profile_picture_id: profile_picture_id || undefined,
            onboarding_completed_steps: onboardingCompletedSteps,
          };
          const profileRes = await updateHalfProfile(profileInfoPayload);
          if (profileRes?.status === 200) {
            if (isStepCompleted(currentStep)) {
              setEditingStepHash(null);
              setIsLoading(false);
              return;
            }
            const completedStepsArray =
              profileRes?.data?.data?.onboarding_completed_steps ??
              profileRes?.data?.onboarding_completed_steps ??
              [];
            if (Array.isArray(completedStepsArray) && completedStepsArray.length > 0) {
              const completedStepsObject = mapBackendStepsToFrontend(
                completedStepsArray,
                STORE_ONBOARDING_STEP_CONFIG
              );
              setOnboardingStepCompletion(completedStepsObject);
              localStorage.setItem("onboardingStepCompletion", JSON.stringify(completedStepsObject));
            } else {
              markStepCompleted(currentStep);
            }
            goToNextStep();
          }
          setIsLoading(false);
          return;
        }

        // Store-details: build store payload from form values (prefer newData; use ?? so 0 is not overwritten)
        const discountValue = newData.discountGiven ?? savedData.store?.discount_given ?? 0;
        const discountTypeValue = newData.discountType ?? savedData?.store?.discount_type ?? "AMOUNT";

        let discountConfigValue = newData?.discount_config ?? savedData.store?.discount_config ?? null;
        if (discountConfigValue) {
          discountConfigValue = {
            ...discountConfigValue,
            discount: discountValue,
            discount_type: discountTypeValue,
          };
        }

        const storeDataObj = {
          discount_config: discountConfigValue,
          demo_commission: numberOrFallback(newData.demoCommission, savedData.store?.demo_commission, 0),
          demo_commission_type: newData.commissionType ?? savedData.store?.demo_commission_type ?? "AMOUNT",
          demo_sales_commission: numberOrFallback(newData.salesCommission, savedData.store?.demo_sales_commission, 0),
          demo_sales_commission_type: newData.salesCommissionType ?? savedData?.store?.demo_sales_commission_type ?? "AMOUNT",
          discount_type: discountTypeValue,
          discount_given: discountValue,
          payment_frequency: numberOrFallback(newData.paymentFrequency, savedData.store?.payment_frequency, 1),
          no_show_demo_commission_type: newData.no_show_demo_commission_type ?? savedData?.store?.no_show_demo_commission_type ?? "AMOUNT",
          no_show_demo_commission: numberOrFallback(newData.no_show_demo_commission, savedData.store?.no_show_demo_commission, 0),
        };

        const formattedData = {
          ...newData,
          profile_picture_id,
          store: storeDataObj,
          onboarding_completed_steps: onboardingCompletedSteps,
        };

        const profileRes = await updateHalfProfile(formattedData);
        if (profileRes?.status === 200) {
          const updatedProfile = profileRes?.data?.data || {};
          // if (updatedProfile.store) {
          //   const currentProfile = JSON.parse(localStorage.getItem("userProfile")) || {};
          //   localStorage.setItem(
          //     "userProfile",
          //     JSON.stringify({
          //       ...currentProfile,
          //       store: {
          //         ...currentProfile.store,
          //         discount_config: updatedProfile.store.discount_config || newData.discount_config || null,
          //       },
          //     })
          //   );
          // }
          const mergedStore = {
            ...(formDataState?.store || {}),
            ...storeDataObj,                          // submitted form values
            ...(updatedProfile.store || {}),          // authoritative server values win
          };
          const mergedState = {
            ...formDataState,
            ...newData,
            store: mergedStore,
          };
          setFormDataState(mergedState);

          // Sync localStorage fully (not just discount_config)
          const currentProfile = JSON.parse(localStorage.getItem("userProfile")) || {};
          localStorage.setItem(
            "userProfile",
            JSON.stringify({
              ...currentProfile,
              ...mergedState,
              store: mergedStore,
            })
          );

          if (isStepCompleted(currentStep)) {
            setEditingStepHash(null);
            setIsLoading(false);
            return;  // Now safe to return — state is already updated above
          }

          const completedStepsArray =
            profileRes?.data?.data?.onboarding_completed_steps ??
            profileRes?.data?.completedSteps ??
            profileRes?.data?.onboarding_completed_steps ??
            [];
          if (Array.isArray(completedStepsArray) && completedStepsArray.length > 0) {
            const completedStepsObject = mapBackendStepsToFrontend(
              completedStepsArray,
              STORE_ONBOARDING_STEP_CONFIG
            );
            setOnboardingStepCompletion(completedStepsObject);
            localStorage.setItem("onboardingStepCompletion", JSON.stringify(completedStepsObject));
          } else {
            markStepCompleted(currentStep);
          }

          if (currentStep === "store-details") {
            const stepsWithDots = onboardingCompletedSteps.includes("dots-account")
              ? onboardingCompletedSteps
              : [...onboardingCompletedSteps, "dots-account"];
            try {
              await updateHalfProfile({ onboarding_completed_steps: stepsWithDots });
              markStepCompleted("dots-account");
              setOnboardingStepCompletion((prev) => {
                const next = { ...prev, "dots-account": true };
                localStorage.setItem("onboardingStepCompletion", JSON.stringify(next));
                return next;
              });
            } catch (e) {
              console.error("Half-profile update for dots step failed", e);
            }
            navigate("/user/settings?page=adminDots&fromOnboarding=true");
          } else {
            goToNextStep();
          }
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error during profile update:", error);
        setIsLoading(false);
        messageApi.open({
          type: 'error',
          content: STORE_PROFILE.GENERAL_ERROR,
          duration: 5,
        });
      }
      return;
    }
  };

  // Render current step component based on step hash
  const CurrentStepComponent = hashToComponentMap[currentStep];
  const isAgreementStep = currentStep === "agreement";
  const currentIndex = stepsConfig.findIndex((s) => s.hash === currentStep);
  const isLastStep = currentIndex === stepsConfig.length - 1;

  const stepProps = {
    next: handleNextStep,
    data: formDataState,
    key: currentStep,
    isCompleted: isStepCompleted(currentStep),
  };

  if (isAgreementStep) {
    // For agreement, if completed, provide onNextStep to navigate forward
    if (stepProps.isCompleted) {
      stepProps.onNextStep = goToNextStep;
    }
  } else {
    stepProps.isEditing = editingStepHash === currentStep;
    stepProps.onStartEdit = () => setEditingStepHash(currentStep);
    stepProps.onCancelEdit = () => setEditingStepHash(null);
    
    // Provide next step navigation if completed and NOT the last step
    if (stepProps.isCompleted && !isLastStep) {
      stepProps.onNextStep = goToNextStep;
    }
  }

  // When on a non-agreement step without agreement completed, show message and go to agreement
  const showCompleteAgreementFirst = !isAgreementStep && !isAgreementComplete();

  return (
    <div>
      {contextHolder}
      {isLoading ? (
        <PurebrandLoader />
      ) : showCompleteAgreementFirst ? (
        <div style={{ padding: "40px 20px", textAlign: "center" }}>
          <InfoCircleOutlined style={{ fontSize: "48px", color: "#faad14", marginBottom: "20px" }} />
          <h3 style={{ marginBottom: "16px", fontSize: "20px", fontWeight: "600" }}>Complete Agreement First</h3>
          <p style={{ fontSize: "16px", color: "#666", marginBottom: "24px" }}>
            You need to review and accept the terms before you can continue with the rest of the steps.
          </p>
          <button
            onClick={() => {
              setCurrentStep("agreement");
              syncHashToStep("agreement");
            }}
            style={{
              backgroundColor: "#1890ff",
              color: "white",
              border: "none",
              padding: "10px 24px",
              borderRadius: "4px",
              fontSize: "14px",
              cursor: "pointer",
              fontWeight: "500"
            }}
          >
            Go to Agreement
          </button>
        </div>
      ) : CurrentStepComponent ? (
        <CurrentStepComponent {...stepProps} />
      ) : (
        <div>Invalid step</div>
      )}
    </div>
  );
};

export default StoreProfileForm;
